import { useMemo, useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Calendar, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, X } from "lucide-react";

// ── helpers ───────────────────────────────────────────────────────────────────

const MONTHS_PL = [
  "styczeń", "luty", "marzec", "kwiecień", "maj", "czerwiec",
  "lipiec", "sierpień", "wrzesień", "październik", "listopad", "grudzień",
];
const DAYS_PL = ["Pn", "Wt", "Śr", "Cz", "Pt", "So", "Nd"];

/** Build an ISO `yyyy-mm-dd` string from a Date using LOCAL getters.
 *  toISOString() would shift to UTC and can return the previous day in +02:00. */
function toISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Parse `yyyy-mm-dd` into a LOCAL midnight Date. Returns null for empty/invalid. */
function parseISO(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Display format: dd.mm.yyyy (matches the format users see in native pl date inputs). */
function formatDisplay(d: Date): string {
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}

/** Expand a typed year: 4 digits verbatim, 2 digits → 20xx (or 19xx for 70..99). */
function expandYear(raw: string): number {
  const n = Number(raw);
  if (raw.length === 4) return n;
  return n < 70 ? 2000 + n : 1900 + n;
}

/** Parse what the user typed into the field. Accepts (separators: . - / space):
 *  25.07.2027 · 25-7-27 · 25.07 (current year) · 25072027 · 250727 · 2027-07-25.
 *  Returns null when the text is not a real calendar date. */
export function parseTypedDate(raw: string, todayYear: number): Date | null {
  const s = raw.trim();
  if (!s) return null;

  let day: number, month: number, year: number;
  const iso = /^(\d{4})[-./ ](\d{1,2})[-./ ](\d{1,2})$/.exec(s);
  const dmy = /^(\d{1,2})[-./ ](\d{1,2})(?:[-./ ](\d{2}|\d{4}))?$/.exec(s);
  const compact = /^(\d{2})(\d{2})(\d{2}|\d{4})$/.exec(s);

  if (iso) {
    [year, month, day] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  } else if (dmy) {
    [day, month] = [Number(dmy[1]), Number(dmy[2])];
    year = dmy[3] ? expandYear(dmy[3]) : todayYear;
  } else if (compact) {
    [day, month, year] = [Number(compact[1]), Number(compact[2]), expandYear(compact[3])];
  } else {
    return null;
  }

  if (year < 1900 || year > 2999) return null;
  const d = new Date(year, month - 1, day);
  // Round-trip guards against overflow dates like 31.02 silently becoming 03.03.
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return d;
}

/** Digit capacity of each `dd.mm.yyyy` segment. */
const MASK_LIMITS = [2, 2, 4];
const SEPARATORS = "-./, ";

/** Reshape what the user typed into the `dd.mm.yyyy` skeleton while they type.
 *  Dots appear on their own once a segment fills up, typing a separator early
 *  (`1.`) pads that segment with a leading zero, and anything that is not a
 *  digit is dropped — so the field can never hold text that is not a date. */
export function maskTypedDate(raw: string): string {
  // A pasted ISO date keeps its meaning instead of being read as dd.mm.yyyy.
  const isoPaste = /^\s*(\d{4})[-./](\d{1,2})[-./](\d{1,2})\s*$/.exec(raw);
  if (isoPaste) {
    return `${isoPaste[3].padStart(2, "0")}.${isoPaste[2].padStart(2, "0")}.${isoPaste[1]}`;
  }

  const segs: string[] = [];
  let cur = "";
  let trailingSep = false;
  for (const ch of raw) {
    if (segs.length === MASK_LIMITS.length) break;
    if (ch >= "0" && ch <= "9") {
      trailingSep = false;
      cur += ch;
      if (cur.length === MASK_LIMITS[segs.length]) {
        segs.push(cur);
        cur = "";
      }
    } else if (SEPARATORS.includes(ch)) {
      if (cur) {
        // Day and month get zero-padded; a short year stays as typed (25.07.28).
        segs.push(segs.length < 2 ? cur.padStart(2, "0") : cur);
        cur = "";
        trailingSep = true;
      } else if (segs.length) {
        trailingSep = true;
      }
    }
  }

  let out = segs.join(".");
  if (cur) out += (segs.length ? "." : "") + cur;
  else if (trailingSep && segs.length && segs.length < MASK_LIMITS.length) out += ".";
  return out;
}

/** Keep the caret over the same digit after the mask reshaped the text. */
function mapCaret(raw: string, masked: string, pos: number): number {
  const digitsBefore = raw.slice(0, pos).replace(/\D/g, "").length;
  if (digitsBefore === 0) return 0;
  let seen = 0;
  for (let i = 0; i < masked.length; i++) {
    if (masked[i] >= "0" && masked[i] <= "9" && ++seen === digitsBefore) {
      let caret = i + 1;
      while (masked[caret] === ".") caret++; // land after the separator, not on it
      return caret;
    }
  }
  return masked.length;
}

function startOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

/** Days of the visible month grid, padded to whole Monday-first weeks. */
function buildGrid(viewYear: number, viewMonth: number): Date[] {
  const first = new Date(viewYear, viewMonth, 1);
  const lead = (first.getDay() || 7) - 1; // Monday-first offset
  const start = new Date(viewYear, viewMonth, 1 - lead);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    cells.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }
  return cells;
}

// ── component ─────────────────────────────────────────────────────────────────

export interface DatePickerProps {
  /** Controlled value as `yyyy-mm-dd` (or "" / null for empty). */
  value: string | null | undefined;
  /** Emits the new `yyyy-mm-dd` string, or "" when cleared. */
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Inclusive bounds as `yyyy-mm-dd`. Days outside are not selectable. */
  min?: string;
  max?: string;
  /** Show the inline clear (×) affordance. Default true. */
  clearable?: boolean;
  className?: string;
  id?: string;
  "aria-label"?: string;
}

const DROPDOWN_H = 320;

export function DatePicker({
  value,
  onChange,
  placeholder = "— wybierz datę —",
  disabled = false,
  min,
  max,
  clearable = true,
  className = "",
  id,
  "aria-label": ariaLabel,
}: DatePickerProps) {
  const selected = useMemo(() => parseISO(value), [value]);
  const minDate = useMemo(() => parseISO(min), [min]);
  const maxDate = useMemo(() => parseISO(max), [max]);

  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState<{ top: number; left: number; width: number } | null>(null);
  /** Raw text while the user is typing; null means "show the committed value". */
  const [draft, setDraft] = useState<string | null>(null);
  // Which month the grid is showing; seeded from the value or today.
  const [view, setView] = useState(() => {
    const base = selected ?? new Date();
    return { year: base.getFullYear(), month: base.getMonth() };
  });

  const fieldRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  /** Caret position to restore after the mask rewrote the text. */
  const caretRef = useRef<number | null>(null);
  /** Last value this picker emitted, to tell the echo of the user's own input apart
   *  from a value the parent set itself (a filter reset, a reloaded form). */
  const emittedRef = useRef(value ?? "");
  const today = useMemo(() => startOfDay(new Date()), []);

  // The mask replaces the whole input value, which would otherwise park the caret
  // at the end on every keystroke.
  useLayoutEffect(() => {
    const pos = caretRef.current;
    caretRef.current = null;
    if (pos != null && inputRef.current && document.activeElement === inputRef.current) {
      inputRef.current.setSelectionRange(pos, pos);
    }
  });

  // A value set from outside replaces half-typed text; otherwise e.g. "Wyczyść filtry"
  // fired while the field still has focus would leave the old date on screen.
  useEffect(() => {
    const next = value ?? "";
    if (next === emittedRef.current) return;
    emittedRef.current = next;
    setDraft(null);
  }, [value]);

  function emit(next: string) {
    emittedRef.current = next;
    onChange(next);
  }

  // Re-seed the visible month whenever the popup opens, so it lands on the current value.
  const openDrop = useCallback(() => {
    const rect = fieldRef.current?.getBoundingClientRect();
    if (!rect) return;
    const base = parseISO(value) ?? new Date();
    setView({ year: base.getFullYear(), month: base.getMonth() });
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow >= DROPDOWN_H + 8 ? rect.bottom + 4 : rect.top - DROPDOWN_H - 4;
    // The popup is at least 260px wide; a compact field near the right edge would
    // push it off-screen, so keep it inside the viewport.
    const width = Math.max(rect.width, 260);
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    setDropPos({ top: Math.max(8, top), left, width });
    setOpen(true);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const target = e.target as Node;
      // The popup lives in a portal outside containerRef, so check it separately.
      const inContainer = containerRef.current?.contains(target);
      const inPopup = popupRef.current?.contains(target);
      if (!inContainer && !inPopup) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const grid = useMemo(() => buildGrid(view.year, view.month), [view]);

  function isDisabledDay(d: Date): boolean {
    if (minDate && d < minDate) return true;
    if (maxDate && d > maxDate) return true;
    return false;
  }

  function pick(d: Date) {
    if (isDisabledDay(d)) return;
    setDraft(null);
    emit(toISO(d));
    setOpen(false);
    inputRef.current?.blur();
  }

  /** Typing: the mask fixes up the text on every keystroke, and the value is committed
   *  as soon as it spells a real, in-range date. */
  function handleType(raw: string, selectionStart: number | null) {
    const masked = maskTypedDate(raw);
    // Typing at the end (the usual case) keeps the caret there even when the mask
    // padded a segment; edits in the middle stay on the digit they were on.
    caretRef.current =
      selectionStart == null || selectionStart >= raw.length ? masked.length : mapCaret(raw, masked, selectionStart);
    setDraft(masked);

    // A half-typed year (2, 20, 202) would otherwise commit as year 2 / 2020 and
    // yank the calendar to it, so only complete input commits mid-typing.
    const digits = masked.replace(/\D/g, "").length;
    if (digits !== 4 && digits !== 8) return;
    const parsed = parseTypedDate(masked, today.getFullYear());
    if (!parsed || isDisabledDay(parsed)) return;
    setView({ year: parsed.getFullYear(), month: parsed.getMonth() });
    const iso = toISO(parsed);
    if (iso !== value) emit(iso);
  }

  /** Leaving the field: empty clears, a valid date commits, anything else reverts. */
  function commitDraft() {
    if (draft == null) return;
    const raw = draft.trim();
    setDraft(null);
    if (!raw) {
      if (value) emit("");
      return;
    }
    const parsed = parseTypedDate(raw, today.getFullYear());
    if (parsed && !isDisabledDay(parsed)) {
      const iso = toISO(parsed);
      if (iso !== value) emit(iso);
    }
  }

  const draftInvalid =
    draft != null &&
    draft.trim() !== "" &&
    (() => {
      const parsed = parseTypedDate(draft, today.getFullYear());
      return !parsed || isDisabledDay(parsed);
    })();

  const text = draft ?? (selected ? formatDisplay(selected) : "");

  function shiftMonth(delta: number) {
    setView((v) => {
      const m = v.month + delta;
      return { year: v.year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 };
    });
  }

  return (
    <div ref={containerRef} className="relative">
      <div
        ref={fieldRef}
        className={[
          "input-base flex items-center gap-2",
          disabled
            ? "bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] cursor-not-allowed"
            : draftInvalid
              ? "border-[var(--color-primary)]"
              : "focus-within:border-[var(--color-primary)]",
          className,
        ].join(" ")}
        // Clicks on the padding around the input still land on the field.
        onClick={(e) => { if (e.target === e.currentTarget && !disabled) inputRef.current?.focus(); }}
      >
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          aria-label="Pokaż kalendarz"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => (open ? setOpen(false) : openDrop())}
          className={`flex-shrink-0 text-[var(--color-text-muted)] ${disabled ? "cursor-not-allowed" : "cursor-pointer hover:text-[var(--color-primary)]"}`}
        >
          <Calendar size={15} />
        </button>
        <input
          ref={inputRef}
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          disabled={disabled}
          aria-label={ariaLabel}
          placeholder={placeholder}
          maxLength={10}
          value={text}
          onChange={(e) => handleType(e.target.value, e.target.selectionStart)}
          onFocus={(e) => { e.target.select(); if (!open) openDrop(); }}
          onBlur={(e) => {
            commitDraft();
            // Keep the popup up while focus stays inside the field or the calendar.
            const next = e.relatedTarget as Node | null;
            if (!next || !(containerRef.current?.contains(next) || popupRef.current?.contains(next))) {
              setOpen(false);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault(); // never submit the surrounding form from here
              commitDraft();
              setOpen(false);
            } else if (e.key === "Escape") {
              setDraft(null);
              setOpen(false);
            }
          }}
          className="flex-1 min-w-0 bg-transparent border-0 p-0 outline-none placeholder:text-[var(--color-text-muted)] disabled:cursor-not-allowed"
          style={{ font: "inherit", color: "inherit" }}
        />
        {clearable && (selected || draft) && !disabled && (
          <button
            type="button"
            tabIndex={-1}
            aria-label="Wyczyść datę"
            onClick={() => { setDraft(null); emit(""); }}
            className="text-gray-300 hover:text-[var(--color-primary)] transition-colors flex-shrink-0"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && dropPos && createPortal(
        <div
          ref={popupRef}
          role="dialog"
          aria-label="Kalendarz"
          style={{ position: "fixed", top: dropPos.top, left: dropPos.left, width: dropPos.width, zIndex: 9999 }}
          className="bg-[var(--color-bg-surface)] border border-[var(--color-border-strong)] shadow-lg p-2"
        >
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); shiftMonth(-12); }}
                aria-label="Poprzedni rok"
                className="p-1 hover:bg-[var(--color-primary-dim)] text-[var(--color-text-secondary)]"
              >
                <ChevronsLeft size={16} />
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); shiftMonth(-1); }}
                aria-label="Poprzedni miesiąc"
                className="p-1 hover:bg-[var(--color-primary-dim)] text-[var(--color-text-secondary)]"
              >
                <ChevronLeft size={16} />
              </button>
            </div>
            <span className="text-sm font-mono font-semibold capitalize">
              {MONTHS_PL[view.month]} {view.year}
            </span>
            <div className="flex items-center">
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); shiftMonth(1); }}
                aria-label="Następny miesiąc"
                className="p-1 hover:bg-[var(--color-primary-dim)] text-[var(--color-text-secondary)]"
              >
                <ChevronRight size={16} />
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); shiftMonth(12); }}
                aria-label="Następny rok"
                className="p-1 hover:bg-[var(--color-primary-dim)] text-[var(--color-text-secondary)]"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>

          {/* Weekday header */}
          <div className="grid grid-cols-7 mb-1">
            {DAYS_PL.map((d) => (
              <div key={d} className="text-center text-[11px] font-mono text-[var(--color-text-muted)] py-1">
                {d}
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {grid.map((d) => {
              const inMonth = d.getMonth() === view.month;
              const isToday = d.getTime() === today.getTime();
              const isSelected = selected != null && d.getTime() === startOfDay(selected).getTime();
              const off = isDisabledDay(d);
              return (
                <button
                  key={toISO(d)}
                  type="button"
                  disabled={off}
                  data-selected={isSelected ? "true" : undefined}
                  onMouseDown={(e) => { e.preventDefault(); pick(d); }}
                  className={[
                    "h-8 text-sm flex items-center justify-center transition-colors",
                    off ? "text-gray-300 dark:text-gray-600 cursor-not-allowed" : "cursor-pointer",
                    !off && isSelected
                      ? "bg-[var(--color-primary)] text-white font-semibold"
                      : !off && isToday
                        ? "text-[var(--color-primary)] font-semibold ring-1 ring-inset ring-[var(--color-primary)]"
                        : "",
                    !off && !isSelected ? "hover:bg-[var(--color-primary-dim)]" : "",
                    inMonth ? "" : "text-[var(--color-text-muted)] opacity-60",
                  ].join(" ")}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          {/* Footer: quick "today" + clear */}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-[var(--color-border-muted)]">
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(today); }}
              disabled={isDisabledDay(today)}
              className="text-xs font-mono text-[var(--color-primary)] hover:underline disabled:text-gray-300 disabled:no-underline"
            >
              Dziś
            </button>
            <span className="text-[10px] font-mono text-[var(--color-text-muted)] truncate px-2">
              wpisz: dd.mm.rrrr
            </span>
            {clearable && selected && (
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); emit(""); setOpen(false); }}
                className="text-xs font-mono text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
              >
                Wyczyść
              </button>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
